
import { Project, statusEnum } from '@data/models/project.model';
import {
    Embedded,
    EmbeddedProject,
} from './embed.model';
import { User } from './user.model';

export type embed = keyof EmbedProperties;

interface EmbedProperties {
    $project?: { id: string; name: string; customer: string; status?: statusEnum };
}

export type EmbedUserRole<T extends embed> = UserRole & Pick<EmbedProperties, T>;

export const userRoleConfiguration: { [K in embed]: { embed: string; transform: (userRole: UserRole, embedded: Embedded) => EmbedProperties[K] } } = {
    $project: {
        embed: 'projects',
        transform: ({ projectId }: UserRole, { projects: embeddedProjects }: Embedded): EmbeddedProject => {
            const project = embeddedProjects[projectId];
            return new EmbeddedProject(project);
        }
    },
};

export type EmbedUserCommunication<T extends embed> = UserCommunication & Pick<EmbedProperties, T>;

export const communicationConfiguration: { [K in embed]: {
    embed: string; transform: (communication: UserCommunication, embedded: Embedded) => EmbedProperties[K];
} } = {
    $project: {
        embed: 'projects',
        transform: ({ projectId }: UserCommunication, { projects: embeddedProjects }: Embedded): EmbeddedProject => {
            const project = embeddedProjects[projectId];
            return new EmbeddedProject(project);
        }
    },
};

export class Authorization {
    userId?: string;
    projectId?: string;
    roles?: string[];
    communicationTypes?: CommTypes[];
    user?: User;
    $project?: Project;
    userInfos?: string;
    updatedAt?: Date;
    updatedBy?: string;
    createdAt?: Date;
    createdBy?: string;
}

export class UserRole {
    projectId: string;
    roles: string[];
    project?: EmbeddedProject;

    public static toCustomUserRole<T extends embed>(data: UserRole, embedded: Embedded, customFields: T[]): EmbedUserRole<T> {
        const customData = customFields.reduce((acc, field: embed) => {
            acc[field] = userRoleConfiguration[field].transform(data, embedded);
            return acc;
        }, {});
        return { ...data, ...customData } as EmbedUserRole<T>;
    }

    public static toCustomUserRoleList<T extends embed>(userRoles: UserRole[], embedded: Embedded, customFields: T[] = []): EmbedUserRole<T>[] {
        return userRoles.map((userRole) => this.toCustomUserRole(userRole, embedded, customFields));
    }
}

export class UserCommunication {
    projectId: string;
    communicationTypes: CommTypes[];
    project?: Project;

    public static toCustomUserCommunication<T extends embed>(data: UserCommunication, embedded: Embedded, customFields: T[]): EmbedUserCommunication<T> {
        const customData = customFields.reduce((acc, field: embed) => {
            acc[field] = communicationConfiguration[field].transform(data, embedded);
            return acc;
        }, {});
        return { ...data, ...customData } as EmbedUserCommunication<T>;
    }

    public static toCustomUserCommunicationList<T extends embed>(
        communications: UserCommunication[], embedded: Embedded, customFields: T[] = []
    ): EmbedUserCommunication<T>[] {
        return communications.map((communication) => this.toCustomUserCommunication(communication, embedded, customFields));
    }
}

export class ProjectRole {
    userId: string;
    roles: string[];
}

export class ProjectCommunication {
    userId: string;
    communicationTypes: CommTypes[];
}

export class ProjectAcl {
    projectId: string;
    acl: Acl[];
}

export class Acl {
    level: LevelEnum;
    entity: string;
}

export type LevelEnum = 'ACCESS_LEVEL_UNKNOWN' | 'READ' | 'EDIT' | 'ADMIN';
export type CommTypes = 'COMM_TYPES_UNKNOWN' | 'REQUEST' | 'INCIDENT' | 'PATCH' | 'CHANGE' | 'MSO' | 'MISC' | 'BILLING';
export enum AccessLevel {
    UNKNOWN = 0,
    READ = 1,
    EDIT = 2,
    ADMIN = 3
}
